import {
  Dispute as DisputeApi,
  DisputeEvidence as DisputeEvidenceApi,
  DisputeEvidenceDetails as DisputeEvidenceDetailsApi,
  DisputeStatus as DisputeStatusApi,
  File as FileApi,
} from '@vendasta/billing';

export enum DisputeStatus {
  WARNING_NEEDS_RESPONSE = 'Warning Needs Response',
  WARNING_UNDER_REVIEW = 'Warning Under Review',
  WARNING_CLOSED = 'Warning Closed',
  NEEDS_RESPONSE = 'Needs Response',
  UNDER_REVIEW = 'Under Review',
  CHARGE_REFUNDED = 'Charge Refunded',
  LOST = 'Lost',
  WON = 'Won',
  UNKNOWN = 'Unknown',
}

export interface Dispute {
  id: string;
  paymentId: string;
  status?: DisputeStatus;
  amount: number;
  created: Date;
  currencyCode: string;
  evidence?: DisputeEvidence;
  evidenceDetails?: DisputeEvidenceDetails;
  isChargeRefundable: boolean;
  reason: string;
  fee: number;
  feeCurrencyCode: string;
}

export interface DisputeEvidence {
  customerCommunication?: File;
  customerSignature?: File;
  shippingDocumentation?: File;
  receipt?: File;
  uncategorizedFile?: File;
  billingAddress: string;
  customerEmailAddress: string;
  customerName: string;
  productDescription: string;
}

export interface DisputeEvidenceRequest {
  billingAddress?: string;
  customerEmailAddress?: string;
  customerName?: string;
  productDescription?: string;
  customerSignatureFileId?: string;
  customerCommunicationFileId?: string;
  receiptFileId?: string;
  shippingDocumentationFileId?: string;
  uncategorizedFileId?: string;
}

export interface File {
  created: Date;
  id: string;
  filename: string;
  url: string;
}

export interface DisputeEvidenceDetails {
  dueBy: Date;
  hasEvidence: boolean;
  pastDue: boolean;
  submissionCount: number;
}

export function disputeFromApi(dispute: DisputeApi): Dispute | undefined {
  if (!dispute) {
    return undefined;
  }
  return {
    id: dispute.id || '',
    paymentId: dispute.paymentId || '',
    status: disputeStatusFromApi(dispute.status),
    amount: dispute.amount || 0,
    created: dispute.created,
    currencyCode: dispute.currencyCode,
    evidence: disputeEvidenceFromApi(dispute.evidence),
    evidenceDetails: disputeEvidenceDetailsFromApi(dispute.evidenceDetails),
    isChargeRefundable: dispute.isChargeRefundable || false,
    reason: dispute.reason || '',
    fee: dispute.fee,
    feeCurrencyCode: dispute.feeCurrencyCode,
  };
}

function disputeEvidenceFromApi(evidence: DisputeEvidenceApi): DisputeEvidence | undefined {
  if (!evidence) {
    return undefined;
  }
  return {
    customerCommunication: fileFromApi(evidence.customerCommunication),
    customerSignature: fileFromApi(evidence.customerSignature),
    shippingDocumentation: fileFromApi(evidence.shippingDocumentation),
    receipt: fileFromApi(evidence.receipt),
    uncategorizedFile: fileFromApi(evidence.uncategorizedFile),
    billingAddress: evidence.billingAddress || '',
    customerEmailAddress: evidence.customerEmailAddress || '',
    customerName: evidence.customerName || '',
    productDescription: evidence.productDescription || '',
  };
}

function fileFromApi(file: FileApi): File | undefined {
  if (!file) {
    return undefined;
  }
  return {
    id: file.id || '',
    created: file.created,
    filename: file.filename || '',
    url: file.url || '',
  };
}

function disputeEvidenceDetailsFromApi(evidence: DisputeEvidenceDetailsApi): DisputeEvidenceDetails | undefined {
  if (!evidence) {
    return undefined;
  }
  return {
    dueBy: evidence.dueBy,
    hasEvidence: evidence.hasEvidence,
    pastDue: evidence.pastDue,
    submissionCount: evidence.submissionCount,
  };
}

export function disputeStatusFromApi(status: DisputeStatusApi): DisputeStatus | undefined {
  switch (status) {
    case DisputeStatusApi.DISPUTE_STATUS_WARNING_NEEDS_RESPONSE:
      return DisputeStatus.WARNING_NEEDS_RESPONSE;
    case DisputeStatusApi.DISPUTE_STATUS_WARNING_UNDER_REVIEW:
      return DisputeStatus.WARNING_UNDER_REVIEW;
    case DisputeStatusApi.DISPUTE_STATUS_WARNING_CLOSED:
      return DisputeStatus.WARNING_CLOSED;
    case DisputeStatusApi.DISPUTE_STATUS_NEEDS_RESPONSE:
      return DisputeStatus.NEEDS_RESPONSE;
    case DisputeStatusApi.DISPUTE_STATUS_UNDER_REVIEW:
      return DisputeStatus.UNDER_REVIEW;
    case DisputeStatusApi.DISPUTE_STATUS_CHARGE_REFUNDED:
      return DisputeStatus.CHARGE_REFUNDED;
    case DisputeStatusApi.DISPUTE_STATUS_LOST:
      return DisputeStatus.LOST;
    case DisputeStatusApi.DISPUTE_STATUS_WON:
      return DisputeStatus.WON;
    default:
      return undefined;
  }
}
